import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { IClientPortalUsersService } from "services/ClientPortalUsersService";
import { TwoFactorAuthSetupModel } from "services/api/cplauthapi/CPLAuthAPI";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";
import { container } from "setup/dependency/inversify.config";
import { RootState } from "setup/redux/RootReducer";

type TwoFactorAuthenticationState = {
  TwoFactorAuthenticationType: number;
  TwoFactorAuthSetupData: TwoFactorAuthSetupModel | undefined;
  Loading: boolean;
  Verifying: boolean;
  SendingCode: boolean;
  SendCodeDisabled: boolean;
};

const initialState: TwoFactorAuthenticationState = {
  TwoFactorAuthenticationType: 0,
  TwoFactorAuthSetupData: undefined,
  Loading: false,
  Verifying: false,
  SendingCode: false,
  SendCodeDisabled: false,
};

export const twoFactorAuthenticationSlice = createSlice({
  name: "twoFactorAuthentication",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getTwoFactorAuthSetupData.rejected, (state) => {
        state.Loading = false;
        state.TwoFactorAuthSetupData = undefined;
      })
      .addCase(getTwoFactorAuthSetupData.pending, (state) => {
        state.Loading = true;
      })
      .addCase(getTwoFactorAuthSetupData.fulfilled, (state, action) => {
        state.Loading = false;
        state.TwoFactorAuthSetupData = action.payload;
      })
      .addCase(verifyOneTimePassword.rejected, (state) => {
        state.Verifying = false;
      })
      .addCase(verifyOneTimePassword.pending, (state) => {
        state.Verifying = true;
      })
      .addCase(verifyOneTimePassword.fulfilled, (state) => {
        state.Verifying = false;
      })
      .addCase(sendTwoFactorAuthEmailSetupCode.rejected, (state) => {
        state.SendCodeDisabled = false;
        state.SendingCode = false;
      })
      .addCase(sendTwoFactorAuthEmailSetupCode.pending, (state) => {
        state.SendingCode = true;
      })
      .addCase(sendTwoFactorAuthEmailSetupCode.fulfilled, (state) => {
        state.SendingCode = false;
        state.SendCodeDisabled = true;
      })
      .addCase(verifyTwoFactorAuthEmailSetup.rejected, (state) => {
        state.Verifying = false;
      })
      .addCase(verifyTwoFactorAuthEmailSetup.pending, (state) => {
        state.Verifying = true;
      })
      .addCase(verifyTwoFactorAuthEmailSetup.fulfilled, (state) => {
        state.Verifying = false;
      });
  },
});

export const getTwoFactorAuthSetupData = createAsyncThunk(
  "TwoFactorAuthentication/GetTwoFactorAuthSetupData",
  async () => {
    const clientPortalUsersService = container.get<IClientPortalUsersService>(SERVICE_TYPES.ClientPortalUsersService);
    return await clientPortalUsersService.getTwoFactorAuthSetupData();
  }
);

export const verifyOneTimePassword = createAsyncThunk(
  "TwoFactorAuthentication/VerifyOneTimePassword",
  async (oneTimePassword: string) => {
    const clientPortalUsersService = container.get<IClientPortalUsersService>(SERVICE_TYPES.ClientPortalUsersService);
    return await clientPortalUsersService.verifyOneTimePasswordSetup(oneTimePassword);
  }
);

export const sendTwoFactorAuthEmailSetupCode = createAsyncThunk(
  "TwoFactorAuthentication/SendTwoFactorAuthEmailSetupCode",
  async () => {
    const clientPortalUsersService = container.get<IClientPortalUsersService>(SERVICE_TYPES.ClientPortalUsersService);
    await clientPortalUsersService.sendTwoFactorAuthEmailSetupCode();
  }
);

export const verifyTwoFactorAuthEmailSetup = createAsyncThunk(
  "TwoFactorAuthentication/VerifyTwoFactorAuthEmailSetup",
  async (verificationCode: string) => {
    const clientPortalUsersService = container.get<IClientPortalUsersService>(SERVICE_TYPES.ClientPortalUsersService);
    return await clientPortalUsersService.verifyTwoFactorAuthEmailSetup(verificationCode);
  }
);

export const selectLoading = (state: RootState) => {
  return state.twoFactorAuthentication.Loading;
};
export const selectSendingCode = (state: RootState) => {
  return state.twoFactorAuthentication.SendingCode;
};
export const selectVerifying = (state: RootState) => {
  return state.twoFactorAuthentication.Verifying;
};
export const selectSendCodeDisabled = (state: RootState) => {
  return state.twoFactorAuthentication.SendCodeDisabled;
};

export const selectTwoFactorAuthSetupData = (state: RootState) => {
  return state.twoFactorAuthentication.TwoFactorAuthSetupData;
};

export default twoFactorAuthenticationSlice.reducer;
